<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import {
    DropdownOption,
    DropdownRenderOption,
    NDropdown,
    NLayout,
    NLayoutSider,
    NLayoutContent,
    NModal,
} from "naive-ui";
import { computed, reactive, watch } from "vue";
import { useRouter } from "vue-router";
import Proper from "../assets/images/proper.svg";
import Feedback from "../components/Feedback.vue";
import { useInactivitySignOut } from "../hooks/useInactivitySignOut";
import { signOut } from "../services/auth";
import { useStorage } from "@vueuse/core";
import { usePortalBreakpoints } from "../hooks/usePortalBreakpoints.ts";
import { useOrganization } from "../hooks/useOrganization.ts";
import { getUserEmail } from "../config/firebase.ts";

const { userNameFromContacts } = useOrganization();
const router = useRouter();

const email = computed(() => getUserEmail() ?? "");
const isSignedIn = computed(() => !!getUserEmail());

const state = reactive({
    showPasswordReset: false,
    showFeedbackModal: false,
});

const settingsOptions = computed<(DropdownOption | DropdownRenderOption)[]>(() => [
    {
        label: `Signed in as ${userNameFromContacts.value} (${email.value})`,
        key: "user-info",
        disabled: true,
    },
    {
        label: "Change Password",
        key: "change-password",
        props: {
            onClick() {
                state.showPasswordReset = true;
            },
        },
    },
    {
        label: "Logout",
        key: "logout",
        props: {
            onClick: onSignOut,
        },
    },
]);

async function onSignOut() {
    await signOut();
    router.push("/login");
}

const {
    isPending: resetPending,
    isSuccess: resetSuccess,
    mutate: onResetPassword,
} = useMutation({
    mutationFn: async function () {
        await sendPasswordResetEmail(getAuth(), email.value);
    },
});

useInactivitySignOut(onSignOut, () => isSignedIn.value);

function useCollapsibleSider() {
    const siderCollapsed = useStorage("sider-collapsed", false);

    const { isDesktop } = usePortalBreakpoints();

    watch(isDesktop, (newVal, oldVal) => {
        // Always expand and lock when switching to desktop
        if (!oldVal && newVal) {
            siderCollapsed.value = false;
        }
        // Collapse when switching to mobile/tablet
        if (oldVal && !newVal) {
            siderCollapsed.value = true;
        }
    });

    return { siderCollapsed, isDesktop };
}
const { siderCollapsed, isDesktop } = useCollapsibleSider();

const appVersion = import.meta.env.VITE_APP_VERSION;
</script>

<template>
    <div class="flex flex-row h-full">
        <n-modal v-model:show="state.showPasswordReset">
            <div class="modal">
                <h1 class="h1">Change Password</h1>
                <p class="font-regular">
                    To change your password we have to verify your email first. We will send you a
                    reset link to your email.
                </p>
                <div v-if="resetSuccess" class="text-lime-700 font-regular">Check your inbox.</div>
                <button
                    v-else
                    :disabled="resetPending"
                    @click="onResetPassword()"
                    class="button w-min"
                >
                    SEND RESET LINK
                </button>
            </div>
        </n-modal>

        <n-modal v-model:show="state.showFeedbackModal">
            <feedback @success="state.showFeedbackModal = false" />
        </n-modal>

        <n-layout has-sider>
            <n-layout-sider
                collapse-mode="width"
                :collapsed-width="0"
                :show-collapsed-content="true"
                :width="94"
                class="bg-cream-dark border-r border-border-gray"
                :collapsed="!isDesktop ? siderCollapsed : false"
            >
                <div class="w-full flex flex-col h-full justify-between">
                    <div class="flex flex-col items-center px-0 gap-1 flex-grow">
                        <button
                            v-if="!isDesktop"
                            @click="siderCollapsed = !siderCollapsed"
                            class="flex items-center justify-center w-7 h-7 rounded-full"
                            :class="{
                                'bg-white border top-2 left-2 absolute': siderCollapsed,
                                'left-4 mt-5': !siderCollapsed,
                            }"
                            aria-label="Toggle menu"
                        >
                            <span class="material-symbols-outlined text-2xl">menu</span>
                        </button>
                        <img
                            class="block rounded-full w-8 h-8 mx-auto mt-3"
                            :src="Proper"
                            alt="Proper Logo"
                        />

                        <hr class="w-full border-neutral-300 my-2" />

                        <nav class="flex flex-col items-left items-center text-center w-full gap-1">
                            <button
                                class="rounded-lg text-dark h-14 w-[70px] flex flex-col items-center justify-center cursor-pointer p-3 hover:bg-cream"
                                :class="{
                                    'bg-cream': $route.path === '/home',
                                }"
                                @click="router.push('/home')"
                            >
                                <span class="text-xl material-symbols-outlined">home</span>
                                <span class="text-[13px] select-none">Home</span>
                            </button>

                            <button
                                class="rounded-lg text-dark h-14 w-[70px] flex flex-col items-center justify-center cursor-pointer p-3 hover:bg-cream"
                                :class="{
                                    'bg-cream': $route.path === '/open-items',
                                }"
                                @click="router.push('/open-items')"
                            >
                                <span class="text-xl material-symbols-outlined">clear_all</span>
                                <span class="text-[12px] select-none whitespace-nowrap"
                                    >Open Items</span
                                >
                            </button>

                            <button
                                class="rounded-lg text-dark h-14 w-[70px] flex flex-col items-center justify-center cursor-pointer p-3 hover:bg-cream"
                                :class="{
                                    'bg-cream': $route.path === '/config',
                                }"
                                @click="router.push('/config')"
                            >
                                <span class="text-xl material-symbols-outlined">settings</span>
                                <span class="text-[13px] select-none">Config</span>
                            </button>
                            <hr class="w-full border-neutral-300 my-2" />
                        </nav>
                    </div>

                    <div
                        class="pt-3 pb-2 flex flex-col w-full border-t border-neutral-300 items-center justify-center gap-1"
                        style="min-height: 64px"
                    >
                        <n-dropdown trigger="hover" :options="settingsOptions" size="small">
                            <button
                                class="text-left uppercase text-xs p-2 w-[70px] rounded-lg flex justify-center gap-2 hover:bg-cream-100"
                            >
                                <img
                                    src="https://lh3.googleusercontent.com/a-/ALV-UjVAkdAgtV7kgF-Hx_bGFDM61JHdQqyui7mfot0k0UDuutgQkIH4HvDT91tqkRfH0riKO_y6UVXrYV7bqM8jCMD_Fdv7CBrEMxMMw73ub-W8dW3fSzwHK3TU2FLU8zQrL-_kMxXboXn_88ofYVurgm_KfGwo-OTl8mgCVRnmd38WqgJZC8uTWRAJMr1mNtQSWa147UsEimpkZvC1Q_7oeCnaJ0V-0uWE7Jgb_LMl37JtuR6XMfFyOlJ-9Woo974xOVzsxiZgrORoHf1dV3ko0DMyFW4zKGxA4_nz9fwMe6TorqxX-fpWK5xAtL5mWrH_cJvYbwNCAvdivjxOMoIbNBaQHisvNJtipo0XixrMp6mmofzCQhK1qxEPG4hbz_q0xEjsC6vlZVhXrQd02YuOg7_oaCh5kIhiFG6h-28QxkDeh4FbAw0ODeeCK4JQKRCguUz5mgDhTsJs9hMND15rkCdwyMZd-XY6Snw8iGzSQ8velu8IwMYVNDXyw3e1PP6JEiQTFA3WRWy0r9GEaLtxlQpBzPG6vXukgopvfBgAR6egMUThIdzbPH4x7ERqWVT0Px_1T8LmE3DzB6zirUPZg__-1xM73X03vJpR274culcjFHJu04A8FVCnpT45I_OceR4Ig-LANkqp78MBJOknwaLpyqqeu2tg3HiwucAjjrbblDhMKOZgouMn0M_5JxobO9Nv0f55J-xix-rWLnAr0Do0PGepwcYwQMQWtMBPiH6tX1gtvhtPqV4tkyEeO-yENW-4lI5nkn1057u2U2kkgZj9yPX8QTqVoyfH_3esaItUOAcEJXk0KhwanpKH01u-EdeGp1aIQMCzDjHzJrxmmqYE85jv4P3UP92S2KonwI8iGPKyhV5I8K_hG5PSWWGhpPX7Wv_TFyq8jyLcUanGOBu3IjoklUtZNB029w9PVsysk90kIwtbUp7viqb0JATbCccHL9DzHzqiwaNcVrE-Ew=s96-c"
                                    class="rounded-full w-8 h-8"
                                    alt="User profile"
                                />
                            </button>
                        </n-dropdown>
                        <span class="text-[10px] text-gray-500">v{{ appVersion }}</span>
                    </div>
                </div>
            </n-layout-sider>
            <n-layout-content>
                <router-view />
            </n-layout-content>
        </n-layout>
    </div>
</template>
